import { Link } from "react-router-dom";

import type { CardState } from "@/api/cards";
import { t, type TrKey } from "@/i18n";
import { clamp } from "@/lib/utils";

import { Pill } from "./ui";

export type AlarmLevel = "normal" | "warning" | "critical";

export interface KpiCardProps {
  title: string;
  state: CardState;
  /** capabilities.py reason kodu (ör. 'henuz_senkron_yok'). */
  reason?: string | null;
  value?: number | null;
  /** Onceden bicimlenmis deger (fmtMoney/fmtPct...) verilirse dogrudan kullanilir. */
  formattedValue?: string;
  /** Degerin altindaki kisa aciklama (ör. "3 kritik · 5 uyari"). */
  sub?: string;
  min?: number;
  max?: number;
  alarmLevel?: AlarmLevel;
  setupHref?: string;
  onAnalyze?: () => void;
  onOpenDetail?: () => void;
}

const STATE_LABEL_KEY: Record<CardState, TrKey> = {
  live: "card.state.live",
  not_connected: "card.state.not_connected",
  not_supported: "card.state.not_supported",
};

const ALARM_CLASS: Record<AlarmLevel, string> = {
  normal: "kpi-ok",
  warning: "kpi-warn",
  critical: "kpi-bad kpi-pulse",
};

/**
 * KPI karti — eski arayuzun `.kpi` karti: ust vurgu cizgisi alarm seviyesiyle
 * renklenir (yesil/sari/kirmizi), deger Space Grotesk, kart tiklaninca detay
 * modali acilir, AI analizi sag ustte kucuk bir dugmedir. "Canli" rozeti
 * yalniz canli OLMAYAN kartlarda gorunur (bagli degil / desteklenmiyor) —
 * canli kartta rozet gurultuden baska bir sey degildi.
 */
export function KpiCard({
  title,
  state,
  reason,
  value,
  formattedValue,
  sub,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  alarmLevel = "normal",
  setupHref,
  onAnalyze,
  onOpenDetail,
}: KpiCardProps) {
  const clampedValue = value == null ? null : clamp(value, min, max);
  const displayValue = formattedValue ?? (clampedValue == null ? "—" : clampedValue.toLocaleString("tr-TR"));
  const isLive = state === "live";
  const canOpenDetail = isLive && Boolean(onOpenDetail);

  return (
    <div
      data-testid="kpi-card"
      data-state={state}
      data-alarm={alarmLevel}
      className={`kpi flex min-h-[118px] flex-col ${isLive ? ALARM_CLASS[alarmLevel] : "kpi-off"} ${canOpenDetail ? "kpi-clickable" : ""}`}
      onClick={canOpenDetail ? onOpenDetail : undefined}
      role={canOpenDetail ? "button" : undefined}
      tabIndex={canOpenDetail ? 0 : undefined}
      onKeyDown={
        canOpenDetail
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenDetail?.();
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="kpi-lbl pr-1">{title}</h3>
        {!isLive && <Pill tone={state === "not_connected" ? "warn" : "muted"}>{t(STATE_LABEL_KEY[state])}</Pill>}
        {isLive && onAnalyze && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAnalyze();
            }}
            title={t("card.analyze")}
            aria-label={`${t("card.analyze")}: ${title}`}
            className="-mr-1 -mt-1 inline-flex min-h-[32px] min-w-[44px] shrink-0 items-center justify-center rounded-md border-[1.5px] border-brand-primary/60 px-2 text-[11px] font-semibold text-brand-primary opacity-70 transition-[opacity,background-color,color] hover:bg-brand-primary hover:text-white hover:opacity-100"
          >
            AI
          </button>
        )}
      </div>

      {isLive ? (
        <div className="mt-2">
          <div className="kpi-val" data-testid="kpi-value">
            {displayValue}
          </div>
          {sub && <div className="kpi-sub">{sub}</div>}
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted">{reason ? t(`card.reason.${reason}`) : t(`empty.${state}.body`)}</p>
      )}

      {state === "not_connected" && setupHref && (
        <div className="mt-auto pt-2 text-xs">
          <Link to={setupHref} className="btn-link min-h-[36px] text-xs" onClick={(e) => e.stopPropagation()}>
            {t("card.setup_link")} →
          </Link>
        </div>
      )}
    </div>
  );
}
